import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Card } from "@/components/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicRecording,
  RecordingChapterDto,
  RoleEnum,
} from "@classview/shared-types";

import { getRecordingQueryKey } from "../api/get-recording";
import { useUpdateRecording } from "../api/update-recording";

/**
 * Format the start time of a chapter to a string in the format of "HH:MM:SS"
 * @param start - The start time of the chapter in seconds
 * @returns The formatted start time
 */
const formatChapterStart = (start: number) => {
  const hours = Math.floor(start / 3600);
  const minutes = Math.floor((start % 3600) / 60);
  const seconds = Math.floor(start % 60);
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * Parse a time string in the format "HH:MM:SS" to seconds
 * @param timeString - The time string to parse
 * @returns The time in seconds, or null if invalid
 */
const parseChapterStart = (timeString: string): number | null => {
  const timeRegex = /^(\d{1,2}):(\d{2}):(\d{2})$/;
  const match = timeString.match(timeRegex);

  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3], 10);

  if (minutes >= 60 || seconds >= 60) return null;

  return hours * 3600 + minutes * 60 + seconds;
};

type RecordingChaptersProps = {
  recordingId: string;
  chapters: RecordingChapterDto[];
  videoRef: React.RefObject<HTMLVideoElement>;
  currentTime: number;
};

export function RecordingChapters(props: RecordingChaptersProps) {
  const { recordingId, chapters, videoRef, currentTime } = props;
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingTime, setEditingTime] = useState("");
  const { organisation, hasPermission } = useOrganisation();
  const { mutateAsync: updateRecording, isPending: isUpdating } =
    useUpdateRecording({
      onSuccess: (updatedRecording) => {
        setEditingIndex(null);
        setEditingTitle("");
        setEditingTime("");
        queryClient.setQueryData(
          getRecordingQueryKey(organisation.id, recordingId),
          (oldData: PublicRecording) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              chapters: updatedRecording.chapters,
            };
          },
        );
        toast.success("Chapter updated successfully");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const formRef = useRef<HTMLFormElement>(null);
  const orderedChapters = [...chapters].sort((a, b) => a.start - b.start);

  const handleUpdateRecording = useCallback(
    async (data: { chapters: RecordingChapterDto[] }) => {
      try {
        await updateRecording({
          orgId: organisation.id,
          id: recordingId,
          data,
        });
      } catch (error) {
        const httpError = error as HttpError;
        if (isBadRequest(httpError)) {
          toast.error(httpError.message.join(", "));
        } else {
          console.error("Unexpected error:", error);
          toast.error("An unexpected error occurred. Please try again.");
        }
      }
    },
    [updateRecording, organisation.id, recordingId],
  );

  const handleSaveChanges = useCallback(async () => {
    if (editingIndex === null) return;

    // Validate that we have both title and time
    if (!editingTitle.trim()) {
      toast.error("Chapter title cannot be empty");
      return;
    }

    if (!editingTime.trim()) {
      toast.error("Chapter time cannot be empty");
      return;
    }

    // Validate time format
    const parsedTime = parseChapterStart(editingTime);
    if (parsedTime === null) {
      toast.error("Invalid time format. Use HH:MM:SS (e.g., 1:23:45)");
      return;
    }

    // Create updated chapters array
    const updatedChapters = [...orderedChapters];
    updatedChapters[editingIndex] = {
      title: editingTitle.trim(),
      start: parsedTime,
    };

    await handleUpdateRecording({ chapters: updatedChapters });
  }, [
    editingIndex,
    editingTitle,
    editingTime,
    orderedChapters,
    handleUpdateRecording,
  ]);

  useEffect(() => {
    if (editingIndex === null) return;

    function handleClickOutside(event: MouseEvent) {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        handleCancelEdit();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleCancelEdit();
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSaveChanges();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editingIndex, editingTitle, editingTime, handleSaveChanges]);

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingTitle("");
    setEditingTime("");
  };

  const handleChapterClick = (start: number) => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.currentTime = start;
  };

  const handleRowClick = (index: number) => {
    if (!canEdit || editingIndex !== null) return;

    const chapter = orderedChapters[index];
    if (!chapter) return;

    setEditingIndex(index);
    setEditingTitle(chapter.title);
    setEditingTime(formatChapterStart(chapter.start));

    if (videoRef.current) {
      videoRef.current.currentTime = chapter.start;
    }
  };

  const handleAddChapter = () => {
    if (!canEdit || editingIndex !== null) return;

    // Create a new chapter at the current video time
    const newChapterStart = Math.floor(currentTime || 0);
    const newChapter: RecordingChapterDto = {
      title: "New Chapter",
      start: newChapterStart,
    };

    // Add the new chapter to the end of the array
    const updatedChapters = [...orderedChapters, newChapter];

    // Set editing state to the new chapter (last index)
    const newIndex = updatedChapters.length - 1;
    setEditingIndex(newIndex);
    setEditingTitle(newChapter.title);
    setEditingTime(formatChapterStart(newChapter.start));

    // Update the recording with the new chapter
    handleUpdateRecording({ chapters: updatedChapters });
  };

  const handleDeleteChapter = useCallback(async () => {
    if (editingIndex === null) return;

    // Create updated chapters array without the chapter at editingIndex
    const updatedChapters = orderedChapters.filter(
      (_, index) => index !== editingIndex,
    );

    // Reset editing state
    setEditingIndex(null);
    setEditingTitle("");
    setEditingTime("");

    // Update the recording with the new chapters array
    await handleUpdateRecording({ chapters: updatedChapters });
  }, [editingIndex, orderedChapters, handleUpdateRecording]);

  const canEdit = hasPermission(RoleEnum.CALL_MANAGEMENT);

  if (chapters.length === 0 && !canEdit) {
    return null;
  }

  return (
    <Card className="w-full self-start lg:w-1/3">
      <Card.Header title="Video Chapters" />
      <Card.Content className="flex flex-col gap-2">
        {orderedChapters.map((chapter, index) => (
          <div key={`${chapter.start}-${index}`} className="flex gap-2">
            {editingIndex === index ? (
              <form
                ref={formRef}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveChanges();
                }}
                className="flex w-full flex-col gap-2"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Input
                      className="min-w-[68px] text-sm text-primary"
                      value={editingTime}
                      onChange={(e) => setEditingTime(e.target.value)}
                      disabled={isUpdating}
                      placeholder="HH:MM:SS"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleDeleteChapter}
                      disabled={isUpdating}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="size-4" aria-hidden={true} />
                      <span className="sr-only">Delete chapter</span>
                    </Button>
                  </div>
                  <Textarea
                    className="h-[20px] w-full text-sm"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    disabled={isUpdating}
                    placeholder="Chapter title"
                  />
                </div>
              </form>
            ) : (
              <>
                <a
                  className={cn(
                    buttonVariants({ variant: "link" }),
                    "block h-auto min-w-16 cursor-pointer p-0",
                  )}
                  onClick={() => handleChapterClick(chapter.start)}
                >
                  {formatChapterStart(chapter.start)}
                </a>
                <span
                  className={`text-sm text-foreground ${canEdit ? "cursor-pointer" : ""}`}
                  onClick={() => handleRowClick(index)}
                >
                  {canEdit ? (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <span>{chapter.title}</span>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          Edit Chapter
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    chapter.title
                  )}
                </span>
              </>
            )}
          </div>
        ))}
        {canEdit && (
          <Button
            variant="outline"
            onClick={handleAddChapter}
            disabled={editingIndex !== null || isUpdating}
            className="mt-2"
          >
            <Plus className="mr-2 size-4" aria-hidden={true} />
            Add Chapter at {formatChapterStart(Math.floor(currentTime || 0))}
          </Button>
        )}
      </Card.Content>
    </Card>
  );
}
